import {useNavigate, useParams} from 'react-router-dom';
import Loading from 'shared/migration-components/loading';
import styled, {useTheme} from 'styled-components';
import dateFnsService from 'shared/services/date-fns-service';
import toBrl from 'shared/util/to-brl';
import {IoArrowBack, IoLockClosed, IoStar} from 'react-icons/io5';

import {PiForkKnifeFill} from 'react-icons/pi';

import {MdCalendarMonth, MdLockClock} from 'react-icons/md';

import useBilling from '../hooks/use-billing';
import Table from 'shared/migration-components/table';
import {FaHourglassEnd, FaHourglassStart} from 'react-icons/fa';
import BillingSection from '../components/billing-section';
import {
  OccurenceTargetEnum,
  OccurrenceProps,
  OrderTypeEnum,
  translatedOccurrenceProps,
} from 'modules/orders/types';
import StatusButton from 'shared/components/status-button';
import PaidBillingSelect from '../components/paid-billing-select';
import useBillingOccurrences from '../hooks/use-billing-occurrences';

const NewOrderDetails: React.FC = () => {
  const {id} = useParams();
  const {data, isLoading} = useBilling(id!);
  const billingOccurrencesQuery = useBillingOccurrences(id!);
  const {colors} = useTheme();
  const navigate = useNavigate();

  if (isLoading)
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
        }}>
        <Loading size={22} />
      </div>
    );

  return (
    <Container>
      <Column style={{padding: 15, paddingTop: 0}}>
        <Content>
          <Info>
            <Column>
              <BackButton onClick={() => navigate(-1)}>
                <IoArrowBack color={colors.text} />
                <BackTitle>Voltar</BackTitle>
              </BackButton>
              <Title>Fatura {data.month}</Title>
              <Description>
                {dateFnsService.dateFormat(data.startDate)}
                {' à '}
                {dateFnsService.dateFormat(data.endDate)}
              </Description>
            </Column>
            <Ratting>
              <Column
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <img
                  style={{borderRadius: 25}}
                  width={50}
                  height={50}
                  src={data.company.logo}
                />
              </Column>
              <Column>
                <LeadingText>{data.company.name}</LeadingText>
                <Text>{data.company.phone}</Text>
                {data.company.pixKey && (
                  <Text
                    style={{
                      marginTop: 10,
                      wordWrap: 'break-word',
                      width: 250,
                    }}>
                    Pix: {data.company.pixKey}
                  </Text>
                )}
              </Column>
              <Column style={{alignItems: 'center', gap: 3}}>
                <LeadingText style={{textAlign: 'center', fontSize: 20}}>
                  {data.company.ratting ?? 'N/A'}
                </LeadingText>
                {data.company.ratting && (
                  <Row>
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                    <IoStar color={colors.secondary} />
                  </Row>
                )}
              </Column>
              <Column style={{alignItems: 'center'}}>
                <LeadingText style={{textAlign: 'center', fontSize: 20}}>
                  {data.company.rattings?.length}
                </LeadingText>
                <Text>avaliações</Text>
              </Column>
            </Ratting>
            <Title>Sobre a fatura</Title>
            <List>
              <Row style={{alignItems: 'start', gap: 10}}>
                <MdCalendarMonth size={20} />
                <Column>
                  <Text>Mês</Text>
                  <RegularText>{data.month}</RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <MdLockClock size={20} />
                <Column>
                  <Text>Vencimento</Text>
                  <RegularText>
                    {dateFnsService.dateFormat(data.expiresAt)}
                  </RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <FaHourglassStart size={20} />
                <Column>
                  <Text>Data de início</Text>
                  <RegularText>
                    {dateFnsService.dateFormat(data.startDate)}
                  </RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <FaHourglassEnd size={20} />
                <Column>
                  <Text>Data de fim</Text>
                  <RegularText>
                    {dateFnsService.dateFormat(data.endDate)}
                  </RegularText>
                </Column>
              </Row>
              <Row style={{alignItems: 'start', gap: 10}}>
                <IoLockClosed size={20} />
                <Column>
                  <Text>Fechada?</Text>
                  <RegularText>{data.isClosed ? 'Sim' : 'Não'}</RegularText>
                </Column>
              </Row>
              <PaidBillingSelect {...data} />
              <Row style={{alignItems: 'start', gap: 10}}>
                <PiForkKnifeFill size={20} />
                <Column>
                  <Text>Quantidade de pedidos</Text>
                  <RegularText>{data.numberOfOrders}</RegularText>
                </Column>
              </Row>
            </List>
            <Line />
            {billingOccurrencesQuery.data?.length! > 0 && (
              <Column>
                <Title>Ocorrências</Title>
                <Table<OccurrenceProps>
                  {...{
                    isLoading: billingOccurrencesQuery.isLoading,
                    data: billingOccurrencesQuery.data,
                  }}
                  translatedPropsKey={translatedOccurrenceProps}
                  onRowClick={(row) =>
                    window.open(`/orders/${row.orderId}`, '_blank')
                  }
                  options={[
                    'description',
                    'observation',
                    {
                      value: 'value',
                      column: (row) => (
                        <RegularText
                          style={{
                            fontSize: 12,
                            color:
                              row.payer === OccurenceTargetEnum.COMPANY
                                ? colors.red
                                : colors.success,
                          }}>
                          {toBrl(
                            row.value *
                              (row.payer === OccurenceTargetEnum.COMPANY
                                ? -1
                                : 1),
                          )}
                        </RegularText>
                      ),
                    },
                    {
                      value: 'createdAt',
                      column: (row) =>
                        dateFnsService.dateTimeFormat(row.createdAt),
                    },
                  ]}
                />
              </Column>
            )}
            <Column>
              <Title>Pedidos</Title>
              <Table
                translatedPropsKey="OrderProps"
                data={data.orders}
                onRowClick={(row) => window.open(`/orders/${row.id}`, '_blank')}
                options={[
                  'number',
                  {
                    value: 'user.name',
                    column: (row) => row.guestUser?.name ?? row.user?.name,
                  },
                  {
                    value: 'createdAt',
                    column: (row) =>
                      dateFnsService.dateTimeFormat(row.createdAt),
                  },
                  {
                    value: 'total',
                    column: (row) => toBrl(row.rawTotal),
                  },
                  {
                    value: 'totalDeliveryPrice',
                    column: (row) => toBrl(row.totalDeliveryPrice),
                  },
                  {
                    value: 'type',
                    column: (row) => (
                      <StatusButton
                        text={
                          row.type === OrderTypeEnum.pickup
                            ? 'Retirada'
                            : 'Entrega'
                        }
                        variant={
                          row.type === OrderTypeEnum.pickup ? 'warn' : 'info'
                        }
                      />
                    ),
                  },
                ]}
              />
            </Column>
          </Info>
          <SideBar>
            <Checkout>
              <Title>Resumo da fatura</Title>
              <BillingSection
                {...{
                  label: 'Total vendido',
                  value: data.rawTotal,
                  tooltip:
                    'Valor bruto de vendas feitas através do app, sem descontos ou taxas.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Total considerado',
                  value: data.consideredTotal,
                  tooltip:
                    'Valor base para cálculo de comissões, obtido subtraindo os investimentos da empresa do total vendido.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Taxa do entregador',
                  value: data.totalDeliveryPrice,
                  variant: 'red',
                  tooltip:
                    'Valor destinado ao pagamento do entregador responsável pela entrega.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Taxas e comissões Uau! Delivery',
                  value: data.cityRawAmount,
                  variant: 'red',
                  tooltip:
                    'Comissão da plataforma Uau! Delivery sobre as vendas realizadas e demais serviços.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Dinheiro com a empresa',
                  value: data.retainedEarningsAmount,
                  variant: 'red',
                  tooltip:
                    'Valor pago pelo cliente diretamente à empresa, fora do app (ex: em dinheiro ou maquininha própria).',
                }}
              />
              <BillingSection
                {...{
                  label: 'Ocorrências',
                  value: data.companyOccurrences,
                  variant: data.companyOccurrences > 0 ? 'green' : 'red',
                  tooltip:
                    'Valores extras no processo do pedido. Ex: entrega extra, seguro de pedido e etc...',
                }}
              />
              <BillingSection
                {...{
                  label: 'Investimentos da empresa',
                  value:
                    data.companyProductsDiscount +
                    data.companyCouponDiscount +
                    data.companyFreeDeliveryPrice,
                  variant: 'green',
                  tooltip:
                    'Quantia investida pela empresa em ações como promoções ou cupons para este pedido.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Investimentos Uau! Delivery',
                  value:
                    data.cityProductsDiscount +
                    data.cityCouponDiscount +
                    data.cityFreeDeliveryPrice +
                    data.balanceAmount,
                  variant: 'green',
                  tooltip:
                    'Quantia subsidiada pela própria plataforma Uau! Delivery, como parte de promoções ou campanhas.',
                }}
              />
              <Row style={{justifyContent: 'space-between'}}>
                <Text>Número de pedidos</Text>
                <Text
                  style={{
                    fontWeight: 500,
                  }}>
                  {data.numberOfOrders}
                </Text>
              </Row>
              <Row style={{justifyContent: 'space-between'}}>
                <Text style={{fontSize: 16, fontWeight: 500}}>
                  Total a receber
                </Text>
                <Text
                  style={{
                    color: colors.success,
                    fontSize: 16,
                    fontWeight: 500,
                  }}>
                  {toBrl(data.companyAmount)}
                </Text>
              </Row>
              <div
                style={{
                  height: 1,
                  backgroundColor: 'rgba(128, 128, 128, .4)',
                  width: '100%',
                }}
              />
              <Title style={{fontSize: 14}}>Custos Uau! Delivery</Title>
              <BillingSection
                {...{
                  label: 'Taxa de transação',
                  value: data.operationFee,
                  variant: 'red',
                  tooltip:
                    'Custo de processamento financeiro (ex: cartão de crédito, boleto, Pix).',
                }}
              />
              <BillingSection
                {...{
                  label: 'Atencipação plano semanal',
                  value: data.advanceFee,
                  variant: 'red',
                  tooltip:
                    'Tarifa cobrada para adiantar o repasse financeiro dos valores da semana à empresa.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Custo semanal',
                  value: data.fixedCost,
                  variant: 'red',
                  tooltip:
                    'Valor fixo cobrado semanalmente pela plataforma, referente ao plano contratado.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Cupons de desconto',
                  value: data.cityCouponDiscount,
                  variant: 'red',
                  tooltip:
                    'Total de descontos aplicados via cupom promocional, deduzido do valor a receber.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Promoções',
                  value: data.cityProductsDiscount,
                  variant: 'red',
                  tooltip:
                    'Valor usado em campanhas promocionais associadas a produtos, deduzido do valor a receber.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Cashback',
                  value: data.balanceAmount,
                  variant: 'red',
                  tooltip:
                    'Quantia devolvida ao cliente como cashback, caso aplicável.',
                }}
              />
              <BillingSection
                {...{
                  label: 'Frete grátis',
                  value: data.freeDeliveryPrice,
                  variant: 'red',
                  tooltip:
                    'Valor usado em campanhas promocionais associadas a entrega grátis, deduzido do valor a receber.',
                }}
              />
              <Row style={{justifyContent: 'space-between'}}>
                <Text
                  style={{fontSize: 16, fontWeight: 500, color: colors.red}}>
                  Custo total
                </Text>
                <Text
                  style={{color: colors.red, fontSize: 16, fontWeight: 500}}>
                  -
                  {toBrl(
                    data.operationFee +
                      data.advanceFee +
                      data.fixedCost +
                      data.cityCouponDiscount +
                      data.cityProductsDiscount +
                      data.freeDeliveryPrice +
                      data.balanceAmount,
                  )}
                </Text>
              </Row>
            </Checkout>
          </SideBar>
        </Content>
      </Column>
    </Container>
  );
};

export default NewOrderDetails;

const Container = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.card};
  color: ${(props) => props.theme.colors.text};
`;

export const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  margin-bottom: 15px;

  svg {
    font-size: 24px;
  }
`;

export const BackTitle = styled.h1`
  color: ${(props) => props.theme.colors.text};
  font-size: 18px;
`;

const Content = styled.div`
  width: 1100px;
  display: grid;
  grid-template-columns: 680px 1fr;
  gap: 30px;
`;

const Info = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SideBar = styled.div`
  padding: 15px;
  padding-top: 60px;
  position: sticky;
  top: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Checkout = styled.div`
  border: 1px solid rgba(128, 128, 128, 0.4);
  box-shadow: rgba(0, 0, 0, 0.12) 0px 6px 16px;
  border-radius: 10px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const Description = styled.p``;

const LeadingText = styled.h3`
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  line-height: normal;
`;

const Text = styled.text`
  color: ${(props) => props.theme.colors.text};
  font-weight: 300;
  line-height: normal;
`;

const RegularText = styled.text`
  color: ${(props) => props.theme.colors.text};
  font-weight: 500;
`;

const Line = styled.div`
  height: 0.5px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.border};
`;

const Ratting = styled.div`
  border: 1px solid ${(props) => props.theme.colors.border};
  padding: 20px 15px;
  border-radius: 15px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr;
  gap: 15px;
  align-items: center;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
`;
